import * as R from "ramda";
import * as React from "react";
import styled from "styled-components";

import EditableTitle from "../../App/Common/Components/EditableTitle";
import { fontSizeNumbers } from "../../styles/fontSizes";
import theme from "../../styles/theme";
import { courseUnitViews, fullCourseViews } from "../../utils/courseViews";
import { PreferredLocale } from "../../utils/locales";
import PreviewTabContent from "../pages/CourseAuthor/PreviewTab";
import { H1 } from "../utils/Headings";
import { Assessment } from "./assessment/Assessment.container";
import { PreviewInstruction } from "./instruction/PreviewInstruction.container";
import { ICourseInstructionPartOperations } from "./instruction/types";
import {
  CourseUnitView,
  DefaultNames,
  ICourseEditOperations,
  ICourseNavigationOperations,
  IFullCourse,
  isAssessment,
  isInstruction,
  isUnit,
  Slide,
} from "./types";

const PreviewContainer = styled.div`
  width: ${theme.maxWidth};
  margin-bottom: 1rem;
`;

interface IPreviewUnitProps {
  course: IFullCourse;
  courseId: string;
  currentLocale: PreferredLocale;
  editOperations: ICourseEditOperations;
  navigationOperations: ICourseNavigationOperations;
  unitPath: CourseUnitView[];
  userId: string;
}

const PreviewUnit: React.SFC<IPreviewUnitProps> = props => {
  const { course, currentLocale, unitPath, editOperations } = props;
  const unit = R.last(fullCourseViews.unitsInPath(course, unitPath));

  if (R.isNil(unit)) {
    return null;
  }

  const unitPathForPatches = R.dropLast(1, unitPath);

  return (
    <>
      <H1>
        <EditableTitle
          currentTitle={courseUnitViews.localizedTitle(unit, currentLocale)}
          defaultTitle={DefaultNames.Unit}
          addTitle={editOperations.addTitle(unitPathForPatches, unit)}
          modifyTitle={editOperations.modifyTitle(unitPathForPatches, unit)}
          maxWidth={95}
          fontSize={fontSizeNumbers.h1}
          isMainTitle={true}
        />
      </H1>
      <PreviewTabContent {...props} maxTitleWidth={47.5} />
    </>
  );
};

export interface ISimplePreviewProps {
  course: IFullCourse;
  courseId: string;
  currentLocale: PreferredLocale;
  displayedComponent: Slide | null;
  displayedComponentUnitPath: CourseUnitView[];
  editOperations: ICourseEditOperations;
  navigationOperations: ICourseNavigationOperations;
  userId: string;
}

export const SimplePreview: React.FC<ISimplePreviewProps> = props => {
  const { editOperations, displayedComponent, displayedComponentUnitPath: unitPath } = props;

  if (isAssessment(displayedComponent)) {
    return (
      <PreviewContainer>
        <Assessment {...props} unitPath={unitPath} />
      </PreviewContainer>
    );
  }

  if (isUnit(displayedComponent)) {
    return (
      <PreviewContainer>
        <PreviewUnit {...props} unitPath={unitPath} />
      </PreviewContainer>
    );
  }

  if (isInstruction(displayedComponent)) {
    const operationKeys: Array<keyof ICourseInstructionPartOperations> = [
      "addChoiceExerciseItem",
      "addClozeGap",
      "editDivider",
      "editSpacer",
      "editImage",
      "editDocument",
      "changeBackground",
      "deleteExerciseItem",
      "deleteClozeGap",
      "deleteInstructionPart",
      "editHTMLContent",
      "editVoiceOverMedia",
      "editVoiceOverText",
      "editVoiceOverTime",
      "enableVoiceOverAudio",
      "modifyChoiceExerciseItemCorrect",
      "modifyChoiceExerciseRandom",
      "modifyChoiceExerciseWrapperContent",
      "modifyClozeGapAnswers",
      "modifyClozeGapHint",
      "modifyClozeTemplate",
      "modifyExerciseItemHint",
      "modifyExerciseItemText",
      "substColumnPart",
    ];

    const partOperations = operationKeys.reduce<ICourseInstructionPartOperations>((ops, key) => {
      return {
        ...ops,
        [key]: editOperations[key](unitPath, displayedComponent),
      };
    }, {} as ICourseInstructionPartOperations);

    return (
      <PreviewContainer>
        <PreviewInstruction
          {...props}
          addTitle={editOperations.addTitle(unitPath, displayedComponent)}
          addPart={editOperations.addPart(unitPath, displayedComponent)}
          instruction={displayedComponent}
          modifyTitle={editOperations.modifyTitle(unitPath, displayedComponent)}
          togglePseudoLesson={editOperations.togglePseudoLesson(unitPath, displayedComponent)}
          partOperations={partOperations}
          modifyPageLockSettings={editOperations.modifyPageLockSettings(unitPath, displayedComponent)}
        />
      </PreviewContainer>
    );
  }

  return null;
};
